/**
 * @file FeedList.tsx
 * @description Общая лента событий кабинета: по дням, с липким заголовком
 * дня, «Показать ещё» по курсору (phase19.31, uiux-design.md §4 «Лента»,
 * §4.1; SC46, SC69).
 *
 * Компонент только раскладывает готовые строки (`FeedEntry`): какие события
 * и как их назвать, решает вызывающий (реестр `feed-event-meta.ts` для
 * заказа). Состояния — общими частями списков: заготовки строк, «пусто»,
 * «пусто под фильтром», ошибка с «Повторить». Лента лежит на непрозрачной
 * поверхности (SC69); заголовок дня прилипает под шапкой кабинета
 * (`--shell-sticky-top`), поэтому у рамки нет `overflow-hidden` — он
 * отключил бы прилипание.
 */

import { useMemo, type ReactNode } from "react";

import { feedTimeLabel, groupFeedByDay } from "./feed-days";
import type { FeedEntry } from "./feed-types";
import { FeedRow } from "./FeedRow";
import { Button } from "../ui/button";
import { LIST_SURFACE_CLASS, ListEmpty, ListError } from "../ui/ListStates";
import type { ListEmptySpec, ListFilteredEmptySpec } from "../ui/responsive-list-types";
import { Skeleton } from "../ui/skeleton";
import { getShopTimeZone } from "../../lib/shop-time";
import { cn } from "@/lib/utils";

interface Props {
  entries: FeedEntry[];
  /** Идёт загрузка (заготовки — только пока строк нет). */
  loading: boolean;
  /** Ошибка загрузки первой страницы; при показанных строках не стирает их. */
  error: string | null;
  onRetry: () => void;
  /** Есть ли более старые события (курсор не пуст). */
  hasMore: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Подпись кнопки дозагрузки; по умолчанию «Показать ещё». */
  moreLabel?: string;
  empty: ListEmptySpec;
  /** Включён ли фильтр: пустой список тогда — «пусто под фильтром». */
  filtered?: boolean;
  filteredEmpty?: ListFilteredEmptySpec;
  /** Над лентой: фильтры-пилюли, «+ Заметка». */
  toolbar?: ReactNode;
  /** Под лентой: «Вся история клиента →». */
  footer?: ReactNode;
  "aria-label": string;
  /** Пояс и «сейчас» — для тестов; по умолчанию пояс магазина вкладки. */
  timeZone?: string;
  now?: Date;
}

/** Заготовки строк ленты той же высоты, что настоящие. */
function FeedSkeleton() {
  return (
    <div aria-busy="true" aria-label="Загрузка ленты" className={cn("flex flex-col gap-3 rounded-xl p-4", LIST_SURFACE_CLASS)}>
      <Skeleton className="h-4 w-40" />
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="h-3 w-10" />
          <Skeleton className="size-6 rounded-full" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  );
}

export function FeedList(props: Props) {
  const { entries, loading, error, hasMore, empty, filtered = false } = props;
  const zone = props.timeZone ?? getShopTimeZone();
  const now = props.now;
  const groups = useMemo(() => groupFeedByDay(entries, now ?? new Date(), zone), [entries, now, zone]);

  let body: ReactNode;
  if (entries.length === 0 && error) {
    body = <ListError message={error} onRetry={props.onRetry} />;
  } else if (entries.length === 0 && loading) {
    body = <FeedSkeleton />;
  } else if (entries.length === 0) {
    body = <ListEmpty spec={filtered && props.filteredEmpty ? props.filteredEmpty : empty} filtered={filtered} />;
  } else {
    body = (
      <div
        aria-label={props["aria-label"]}
        role="feed"
        aria-busy={loading || props.loadingMore ? true : undefined}
        className={cn("rounded-xl px-3 [box-shadow:inset_0_0_0_1px_var(--border)] sm:px-4", LIST_SURFACE_CLASS)}
      >
        {groups.map((group) => (
          <section key={group.key} aria-label={group.label}>
            <h3
              className={cn(
                "type-caption sticky top-[var(--shell-sticky-top,0px)] z-[1] -mx-3 border-b border-[var(--border)] px-3 py-2 font-semibold text-on-surface sm:-mx-4 sm:px-4",
                LIST_SURFACE_CLASS,
              )}
            >
              {group.label}
            </h3>
            <ul className="flex flex-col divide-y divide-[var(--border)]">
              {group.entries.map((entry) => (
                <FeedRow key={entry.id} entry={entry} time={feedTimeLabel(entry.at, zone)} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {props.toolbar}
      {body}
      {entries.length > 0 && hasMore && props.onLoadMore ? (
        <Button variant="outline" className="w-full sm:w-auto sm:self-center" loading={props.loadingMore} onClick={props.onLoadMore}>
          {props.moreLabel ?? "Показать ещё"}
        </Button>
      ) : null}
      {props.footer}
    </div>
  );
}
